import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { AuthProvider } from './context/AuthContext';
import { SearchProvider } from './context/SearchContext';
import { AdminProvider } from './context/AdminContext';
import AdminRoute from './components/admin/AdminRoute';
import ScrollToTop from './components/common/ScrollToTop';
import MainLayout from './layouts/MainLayout';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy loaded pages
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetails = lazy(() => import('./pages/ProductDetails'));
const Categories = lazy(() => import('./pages/Categories'));
const CategoryProducts = lazy(() => import('./pages/CategoryProducts'));
const Cart = lazy(() => import('./pages/Cart'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const Search = lazy(() => import('./pages/Search'));

// Admin pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminAddProduct = lazy(() => import('./pages/admin/AdminAddProduct'));
const AdminEditProduct = lazy(() => import('./pages/admin/AdminEditProduct'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AdminProvider>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <SearchProvider>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* Admin Routes */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                    <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
                    <Route path="/admin/products/add" element={<AdminRoute><AdminAddProduct /></AdminRoute>} />
                    <Route path="/admin/products/edit/:id" element={<AdminRoute><AdminEditProduct /></AdminRoute>} />
                    <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
                    <Route path="/admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />

                    {/* Store Routes */}
                    <Route path="/" element={<MainLayout><Home /></MainLayout>} />
                    <Route path="/shop" element={<MainLayout><Shop /></MainLayout>} />
                    <Route path="/product/:id" element={<MainLayout><ProductDetails /></MainLayout>} />
                    <Route path="/categories" element={<MainLayout><Categories /></MainLayout>} />
                    <Route path="/category/:slug" element={<MainLayout><CategoryProducts /></MainLayout>} />
                    <Route path="/cart" element={<MainLayout><Cart /></MainLayout>} />
                    <Route path="/wishlist" element={<MainLayout><Wishlist /></MainLayout>} />
                    <Route path="/checkout" element={<MainLayout><Checkout /></MainLayout>} />
                    <Route path="/order-success" element={<MainLayout><OrderSuccess /></MainLayout>} />
                    <Route path="/login" element={<MainLayout><Login /></MainLayout>} />
                    <Route path="/signup" element={<MainLayout><Signup /></MainLayout>} />
                    <Route path="/forgot-password" element={<MainLayout><ForgotPassword /></MainLayout>} />
                    <Route path="/profile" element={<MainLayout><Profile /></MainLayout>} />
                    <Route path="/search" element={<MainLayout><Search /></MainLayout>} />
                  </Routes>
                </Suspense>
              </SearchProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </AdminProvider>
    </Router>
  );
}

export default App;
